import type { ImageLoaderProps } from 'next/image'

/**
 * Custom `next/image` loader. Replaces `/_next/image` for our own media.
 *
 * Why this exists
 * ---------------
 * `/_next/image` on Cloudflare Workers is served by OpenNext, which resizes with
 * the `IMAGES` binding. That binding bills against the account's Cloudflare
 * Images allowance, and OpenNext does not guard the call: when a transformation
 * is refused (error 9422, "free unique transformations exhausted") the rejection
 * escapes the request handler, the isolate throws, and the browser gets a 500 —
 * a broken image rather than a slightly larger one. That is what took every
 * uncached thumbnail off the site on 2026-09-01.
 *
 * Delivery today (PLAN-requests-performance.md P3.3, decision D2): `image.games-on.net`
 * is an R2 custom domain; the media Worker only owns `/t/*`. R2 ignores `?w=`, so
 * without transformations every width maps to ONE plain URL per object (the key
 * with the GCS-era `%2F` separators normalised to `/`, query dropped). Duplicate
 * URLs in a srcset are legal; the browser picks one candidate and caches one file.
 *
 * Resized delivery (PLAN-pagespeed-90.md F4) is the same host's zone-level
 * transformer: `/cdn-cgi/image/width=W,quality=Q,format=auto,onerror=redirect/<key>`.
 * It is switched on with `NEXT_PUBLIC_IMAGE_TRANSFORMS=cdn-cgi` at build time and
 * off by default, because it only pays off once the account has paid
 * transformations: on the free 5,000/month (spent within hours of a month start
 * at this catalogue size) every new variant answers `cf-resized: err=9422` and
 * `onerror=redirect` turns that into a 307 to the original — never a broken
 * image, but an extra round trip per picture. Flip the flag in the same deploy
 * as enabling billing. `format=auto` is one billable transformation however many
 * encodings it serves; a per-width entry is billed once per calendar month.
 */

/**
 * Delivery host for media in R2. Same variable `utils/image.ts` reads, but
 * defaulted: the loader has to know the host at build time to decide whether a
 * URL is ours, and an unset variable would silently turn every image into a
 * pass-through.
 */
const MEDIA_HOST = process.env.NEXT_PUBLIC_IMAGE_HOST || 'image.games-on.net'

const MEDIA_PREFIX = `https://${MEDIA_HOST}/`

// Inlined by Next at build time (NEXT_PUBLIC_*), so the choice is per deploy.
const TRANSFORMS_ON = process.env.NEXT_PUBLIC_IMAGE_TRANSFORMS === 'cdn-cgi'

// Only formats the transformer can resize without changing what the picture is:
// GIFs would be re-encoded as animations (billable per variant, and a resized
// still frame is the wrong output), SVGs are not raster, AVIF sources are rare
// and already small. Mirrors RASTER_RE in utils/articleHtml.ts.
const RESIZABLE_RE = /\.(jpe?g|png|webp)$/i

// gow-media-worker's ladder (src/index.ts WIDTH_LADDER). Every width `next/image`
// asks for is already on it (next.config.js deviceSizes/imageSizes are subsets);
// hand-written widths snap upward so near-identical requests share one variant.
const WIDTH_LADDER = [16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840]
const snapWidth = (w: number): number => WIDTH_LADDER.find((rung) => rung >= w) ?? WIDTH_LADDER[WIDTH_LADDER.length - 1]

/**
 * Whether `src` is media we can ask the worker to resize.
 *
 * Everything else has to be used at its native size: statically imported assets
 * under /_next/static/media (already hashed and immutable) and the third-party
 * hosts pasted into article bodies over the years. Resizing those would mean
 * proxying and re-encoding someone else's image on our bill, which is what the
 * old `remotePatterns: ['**']` did.
 */
export const isMediaUrl = (src: string): boolean => src.startsWith(MEDIA_PREFIX)

/**
 * The object key behind a media URL, in its canonical spelling. Only `%2F` is
 * normalised (never a full decodeURIComponent): the 96,198 keys contain no
 * spaces, `%`, `#`, `?` or non-ASCII, and the ten `.svg+xml` keys' literal `+`
 * must stay literal. A stored query (there are a few) is dropped. A URL that is
 * already a transform URL yields the key it transforms.
 */
export const mediaObjectKey = (src: string): string => {
  const path = src.slice(MEDIA_PREFIX.length).split('?')[0].replace(/%2F/gi, '/')
  const m = path.match(/^cdn-cgi\/image\/[^/]*\/(.+)$/)
  return m ? m[1] : path
}

/**
 * The plain, never-resized URL of our media — the one spelling every variant of
 * an object shares. Returns `src` unchanged when it is not ours. Use this where
 * the *bytes* matter rather than the display size: the canvas sampler in
 * utils/dominantColor.ts (the transformed response carries no
 * Access-Control-Allow-Origin on the 307 fallback), and as the key of the
 * dimensions map in utils/articleHtml.ts.
 */
export const mediaOriginal = (src: string): string => {
  if (!isMediaUrl(src)) return src
  return `${MEDIA_PREFIX}${mediaObjectKey(src)}`
}

/**
 * Delivery URL for one width. Returns `src` unchanged when it is not ours, so
 * callers can use it unconditionally.
 *
 * Also used by the hand-built srcsets outside `next/image`: the article-body
 * rewriter in `utils/articleHtml.ts` and the LCP preload in
 * `pages/article/[id].tsx`. Both used to build `/_next/image` URLs by hand, so
 * both broke together when the Images allowance ran out; they now share this one
 * function, which is also why the preload's candidates always match the <img>'s.
 */
export const mediaSrc = (src: string, width: number, quality = 75, resize = false): string => {
  if (!isMediaUrl(src)) return src
  const key = mediaObjectKey(src)
  if (!resize || !TRANSFORMS_ON || width <= 0 || !RESIZABLE_RE.test(key)) return `${MEDIA_PREFIX}${key}`
  const q = Math.min(100, Math.max(5, Math.round(quality / 5) * 5))
  return `${MEDIA_PREFIX}cdn-cgi/image/width=${snapWidth(width)},quality=${q},format=auto,onerror=redirect/${key}`
}

/**
 * The default loader (next.config.js `loaderFile`): never resizes. Article body
 * images, avatars, icons and everything else keep the plain object URL.
 */
export default function imageLoader({ src, width, quality }: ImageLoaderProps): string {
  return mediaSrc(src, width, quality ?? 75)
}

/**
 * Card thumbnails on the homepage and the list pages — the images that make
 * those pages 7–20 MB on a phone — opt in to resizing with `loader={cardImageLoader}`.
 * Scoped on purpose (owner decision 2026-09-07): the account stays on Cloudflare
 * Images' free 5,000 unique transformations a month. A few hundred list-page
 * thumbnails at one or two widths each fit; the ~20k article-body images and
 * per-article related cards would not, so they never use this loader. Inert
 * while `NEXT_PUBLIC_IMAGE_TRANSFORMS` is not `cdn-cgi` (September's quota was
 * spent on the 1st; the flag goes on with the October reset).
 */
export function cardImageLoader({ src, width, quality }: ImageLoaderProps): string {
  return mediaSrc(src, width, quality ?? 75, true)
}
